/**
 * QuantumFlowSync.AI - Permission Hook
 * React hook for permission-based UI rendering
 */
import { useAuth } from '../contexts/AuthContext';

/**
 * Plain function to check permission (not a hook - safe to call anywhere)
 */
export const checkPermission = (employee, resource, scope, action) => {
  if (!employee) {
    return false;
  }

  // Super admin has all permissions
  if (employee.tier === 1 && employee.roles?.some(r => r.name === 'super_admin')) {
    return true;
  }

  // Check if employee has the permission
  const permissionString = `${resource}:${scope}:${action}`;

  // Check direct permissions
  if (employee.permissions?.includes(permissionString)) {
    return true;
  }

  // Check wildcard permissions
  const wildcardPermissions = [
    `${resource}:${scope}:*`,
    `${resource}:*:${action}`,
    `${resource}:*:*`,
    `*:*:*`
  ];

  for (const wildcard of wildcardPermissions) {
    if (employee.permissions?.includes(wildcard)) {
      return true;
    }
  }

  return false;
};

/**
 * Check if employee has a specific permission
 */
export const usePermission = (resource, scope, action) => {
  const { employee } = useAuth();
  return checkPermission(employee, resource, scope, action);
};

/**
 * Check if employee has ANY of the specified permissions
 */
export const useAnyPermission = (permissions) => {
  const { employee } = useAuth();

  if (!employee) {
    return false;
  }

  return permissions.some(({ resource, scope, action }) => {
    return checkPermission(employee, resource, scope, action);
  });
};

/**
 * Check if employee has ALL of the specified permissions
 */
export const useAllPermissions = (permissions) => {
  const { employee } = useAuth();

  if (!employee) {
    return false;
  }

  return permissions.every(({ resource, scope, action }) => {
    return checkPermission(employee, resource, scope, action);
  });
};

/**
 * Check if employee is in a specific tier or higher
 */
export const useTier = (tier) => {
  const { employee } = useAuth();

  if (!employee) {
    return false;
  }

  return employee.tier <= tier;
};

/**
 * Check if employee has a specific role
 */
export const useRole = (roleName) => {
  const { employee } = useAuth();

  if (!employee) {
    return false;
  }

  return employee.roles?.some(role => role.name === roleName);
};

/**
 * Get employee's effective permissions grouped by resource
 */
export const usePermissions = () => {
  const { employee } = useAuth();

  if (!employee || !employee.permissions) {
    return {};
  }

  const grouped = {};

  employee.permissions.forEach(permission => {
    const [resource, scope, action] = permission.split(':');

    if (!grouped[resource]) {
      grouped[resource] = [];
    }

    grouped[resource].push({ scope, action, full: permission });
  });

  return grouped;
};

/**
 * Check if a UI element should be disabled (read-only permission)
 */
export const useReadOnly = (resource, scope) => {
  const canUpdate = usePermission(resource, scope, 'update');
  const canCreate = usePermission(resource, scope, 'create');
  const canDelete = usePermission(resource, scope, 'delete');
  const canRead = usePermission(resource, scope, 'read');

  return canRead && !canUpdate && !canCreate && !canDelete;
};

export default usePermission;
